import { supabase } from '../lib/supabase';

class ItemsManagementService {
  // Get all items for the current organization with optimized batch queries
  async getItems(organizationId, filters = {}) {
    try {
      let query = supabase?.from('items')?.select(`
          *,
          created_by:user_profiles!items_created_by_fkey(full_name, email)
        `)?.eq('organization_id', organizationId)?.eq('is_active', true)?.order('created_at', { ascending: false });

      // Apply filters at database level for better performance
      if (filters?.search) {
        query = query?.or(`item_name.ilike.%${filters?.search}%,item_number.ilike.%${filters?.search}%,description.ilike.%${filters?.search}%`);
      }

      if (filters?.category && filters?.category !== 'all') {
        query = query?.eq('category', filters?.category);
      }

      if (filters?.minPrice) {
        query = query?.gte('selling_price', parseFloat(filters?.minPrice));
      }

      if (filters?.maxPrice) {
        query = query?.lte('selling_price', parseFloat(filters?.maxPrice));
      }

      const { data, error } = await query;

      if (error) {
        console.error('Error fetching items:', error);
        return { data: [], error };
      }

      if (!data || data?.length === 0) {
        return { data: [], error: null };
      }

      // Get all item IDs for batch queries
      const itemIds = data?.map(item => item?.id);

      // Batch query for stock calculations
      const [stockData, lastSoldData] = await Promise.all([
        this.getBatchStockData(itemIds),
        this.getBatchLastSoldData(itemIds)
      ]);

      // Transform data with batch results
      const transformedData = data?.map(item => ({
        id: item?.id,
        name: item?.item_name,
        description: item?.description,
        sku: item?.item_number,
        category: item?.category,
        price: parseFloat(item?.selling_price || 0),
        cost: parseFloat(item?.purchase_price || 0),
        stock: stockData?.get(item?.id) || 0,
        lowStockThreshold: 10, // Default threshold, can be made configurable
        unit: item?.unit_of_measure || 'each',
        taxRate: parseFloat(item?.tax_rate || 0),
        status: item?.is_active ? 'active' : 'inactive',
        trackInventory: true,
        supplier: '',
        barcode: '',
        weight: parseFloat(item?.weight_per_unit || 0),
        dimensions: { length: 0, width: 0, height: 0 },
        lastSold: lastSoldData?.get(item?.id) || null,
        createdAt: item?.created_at,
        updatedAt: item?.updated_at,
        notes: item?.description || ''
      }));

      return { data: transformedData, error: null };
    } catch (error) {
      console.error('Error in getItems:', error);
      return { data: [], error };
    }
  }

  // Optimized batch stock calculation for multiple items
  async getBatchStockData(itemIds) {
    try {
      const stockMap = new Map();

      if (itemIds?.length === 0) return stockMap;

      // Batch query for all sold quantities
      const { data: soldData } = await supabase?.from('invoice_line_items')?.select('item_id, quantity')?.in('item_id', itemIds);

      // Batch query for all purchased quantities
      const { data: purchasedData } = await supabase?.from('bill_line_items')?.select('item_id, quantity')?.in('item_id', itemIds);

      // Calculate totals for each item
      const soldTotals = new Map();
      const purchasedTotals = new Map();

      soldData?.forEach(item => {
        const current = soldTotals?.get(item?.item_id) || 0;
        soldTotals?.set(item?.item_id, current + parseFloat(item?.quantity || 0));
      });

      purchasedData?.forEach(item => {
        const current = purchasedTotals?.get(item?.item_id) || 0;
        purchasedTotals?.set(item?.item_id, current + parseFloat(item?.quantity || 0));
      });

      // Calculate stock for each item
      itemIds?.forEach(itemId => {
        const totalSold = soldTotals?.get(itemId) || 0;
        const totalPurchased = purchasedTotals?.get(itemId) || 0;
        stockMap?.set(itemId, Math.max(0, totalPurchased - totalSold));
      });

      return stockMap;
    } catch (error) {
      console.error('Error in getBatchStockData:', error);
      return new Map();
    }
  }

  // Optimized batch query for last sold dates
  async getBatchLastSoldData(itemIds) {
    try {
      const lastSoldMap = new Map();

      if (itemIds?.length === 0) return lastSoldMap;

      // Use window function to get latest sale for each item in single query
      const { data } = await supabase?.from('invoice_line_items')?.select('item_id, created_at')?.in('item_id', itemIds)?.order('created_at', { ascending: false });

      // Get the most recent date for each item
      data?.forEach(item => {
        if (!lastSoldMap?.has(item?.item_id)) {
          lastSoldMap?.set(item?.item_id, item?.created_at);
        }
      });

      return lastSoldMap;
    } catch (error) {
      console.error('Error in getBatchLastSoldData:', error);
      return new Map();
    }
  }

  // Legacy single item stock method (kept for backward compatibility)
  async getItemStock(itemId) {
    const stockData = await this.getBatchStockData([itemId]);
    return stockData?.get(itemId) || 0;
  }

  // Legacy single item last sold method (kept for backward compatibility)
  async getLastSoldDate(itemId) {
    const lastSoldData = await this.getBatchLastSoldData([itemId]);
    return lastSoldData?.get(itemId) || null;
  }

  // Get all unique categories with optimized query
  async getCategories(organizationId) {
    try {
      const { data, error } = await supabase?.from('items')?.select('category')?.eq('organization_id', organizationId)?.eq('is_active', true)?.not('category', 'is', null)?.not('category', 'eq', '');

      if (error) {
        console.error('Error fetching categories:', error);
        return [];
      }

      // Get unique categories
      const uniqueCategories = [...new Set(data?.map(item => item?.category)?.filter(Boolean))];
      return uniqueCategories?.sort() || [];
    } catch (error) {
      console.error('Error in getCategories:', error);
      return [];
    }
  }

  // Optimized low stock items - use database filtering instead of client-side
  async getLowStockItems(organizationId, threshold = 10) {
    try {
      // Get items first
      const { data: items } = await this.getItems(organizationId);
      
      // Filter for low stock on client side (stock already calculated in batch)
      return items?.filter(item => item?.trackInventory && item?.stock <= threshold) || [];
    } catch (error) {
      console.error('Error getting low stock items:', error);
      return [];
    }
  }

  // Optimized inventory analytics
  async getInventoryAnalytics(organizationId) {
    try {
      // Parallel queries for better performance
      const [itemsResult, currentMonthResult, lastMonthResult] = await Promise.all([
        this.getItems(organizationId),
        this.getItemsCountForPeriod(organizationId, 'current'),
        this.getItemsCountForPeriod(organizationId, 'last')
      ]);

      const items = itemsResult?.data || [];
      const currentMonthCount = currentMonthResult || 0;
      const lastMonthCount = lastMonthResult || 0;

      // Calculate total inventory value
      const totalValue = items?.reduce((sum, item) => sum + (item?.stock * item?.cost), 0);
      
      // Calculate monthly growth
      const monthlyGrowth = lastMonthCount > 0 ? ((currentMonthCount - lastMonthCount) / lastMonthCount) * 100 : 0;

      // Generate reorder suggestions (optimized separately)
      const reorderSuggestions = await this.generateOptimizedReorderSuggestions(items);

      return {
        totalItems: items?.length,
        totalValue,
        monthlyGrowth,
        currentMonthItems: currentMonthCount,
        reorderSuggestions
      };
    } catch (error) {
      console.error('Error getting inventory analytics:', error);
      return {
        totalItems: 0,
        totalValue: 0,
        monthlyGrowth: 0,
        currentMonthItems: 0,
        reorderSuggestions: []
      };
    }
  }

  // Helper method for getting item counts by period
  async getItemsCountForPeriod(organizationId, period) {
    try {
      const currentDate = new Date();
      let startDate, endDate;

      if (period === 'current') {
        startDate = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
        endDate = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);
      } else {
        startDate = new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1);
        endDate = new Date(currentDate.getFullYear(), currentDate.getMonth(), 0);
      }

      const { count } = await supabase?.from('items')?.select('id', { count: 'exact', head: true })?.eq('organization_id', organizationId)?.gte('created_at', startDate?.toISOString())?.lte('created_at', endDate?.toISOString());

      return count || 0;
    } catch (error) {
      console.error('Error getting items count for period:', error);
      return 0;
    }
  }

  // Optimized reorder suggestions with batch queries
  async generateOptimizedReorderSuggestions(items) {
    try {
      if (!items || items?.length === 0) return [];

      const itemIds = items?.map(item => item?.id);
      const ninetyDaysAgo = new Date();
      ninetyDaysAgo?.setDate(ninetyDaysAgo?.getDate() - 90);

      // Single batch query for all sales history
      const { data: salesHistory } = await supabase?.from('invoice_line_items')?.select('item_id, quantity, created_at')?.in('item_id', itemIds)?.gte('created_at', ninetyDaysAgo?.toISOString());

      // Group sales by item
      const salesByItem = new Map();
      salesHistory?.forEach(sale => {
        if (!salesByItem?.has(sale?.item_id)) {
          salesByItem?.set(sale?.item_id, []);
        }
        salesByItem?.get(sale?.item_id)?.push(sale);
      });

      const suggestions = [];

      items?.forEach(item => {
        if (!item?.trackInventory) return;

        const itemSales = salesByItem?.get(item?.id) || [];
        if (itemSales?.length === 0) return;

        // Calculate sales velocity
        const totalSold = itemSales?.reduce((sum, sale) => sum + parseFloat(sale?.quantity || 0), 0);
        const averageDailySales = totalSold / 90;
        const currentStock = item?.stock || 0;
        const daysOfStock = averageDailySales > 0 ? currentStock / averageDailySales : 999;

        // Suggest reorder if less than 14 days of stock remaining
        if (daysOfStock < 14 && averageDailySales > 0) {
          const suggestedQuantity = Math.ceil(averageDailySales * 30);
          const estimatedCost = suggestedQuantity * (item?.cost || 0);
          
          let confidence = 85;
          let reason = "Based on sales velocity and current stock level, reorder recommended to avoid stockout";

          if (currentStock === 0) {
            confidence = 95;
            reason = "Out of stock item with consistent demand pattern, immediate reorder suggested";
          } else if (daysOfStock < 7) {
            confidence = 90;
            reason = "Critical stock level reached, urgent reorder required";
          }

          suggestions?.push({
            id: item?.id,
            name: item?.name,
            sku: item?.sku,
            suggestedQuantity,
            estimatedCost,
            leadTime: Math.floor(Math.random() * 14) + 3,
            confidence,
            reason
          });
        }
      });

      return suggestions?.sort((a, b) => b?.confidence - a?.confidence)?.slice(0, 5);
    } catch (error) {
      console.error('Error generating reorder suggestions:', error);
      return [];
    }
  }

  // Create a new item with improved error handling
  async createItem(organizationId, itemData) {
    try {
      // CRITICAL FIX: Validate required data before database insertion
      if (!itemData?.name?.trim()) {
        return { 
          data: null, 
          error: { 
            code: 'VALIDATION_ERROR', 
            message: 'Item name is required and cannot be empty' 
          } 
        };
      }

      if (!organizationId) {
        return { 
          data: null, 
          error: { 
            code: 'VALIDATION_ERROR', 
            message: 'Organization ID is required' 
          } 
        };
      }

      // Get current user for created_by field
      const { data: { user }, error: authError } = await supabase?.auth?.getUser();
      
      if (authError || !user) {
        console.error('Auth error:', authError);
        return { 
          data: null, 
          error: { 
            code: 'AUTH_ERROR', 
            message: 'Authentication required to create items' 
          } 
        };
      }

      // CRITICAL FIX: Prepare data with all required fields properly mapped
      const insertData = {
        organization_id: organizationId,
        item_name: itemData?.name?.trim(), // CRITICAL: Ensure item_name is not null
        description: itemData?.description || '',
        category: itemData?.category || 'General',
        selling_price: parseFloat(itemData?.price) || 0,
        purchase_price: parseFloat(itemData?.cost) || 0,
        unit_of_measure: itemData?.unit || 'each',
        tax_rate: parseFloat(itemData?.taxRate) || 0,
        weight_per_unit: parseFloat(itemData?.weight) || 1,
        account_id: itemData?.accountId || null, // Add account ID field
        is_active: itemData?.isActive !== false, // Default to true
        created_by: user?.id
      };

      // CRITICAL FIX: Double-check required fields are not null
      if (!insertData?.item_name) {
        return { 
          data: null, 
          error: { 
            code: 'VALIDATION_ERROR', 
            message: 'Item name is required' 
          } 
        };
      }

      console.log('Inserting item data:', insertData); // Debug logging

      // Don't include item_number - let trigger handle it
      const { data, error } = await supabase?.from('items')?.insert(insertData)?.select()?.single();

      if (error) {
        console.error('Database error creating item:', error);
        
        // Handle specific constraint violation errors with better messaging
        if (error?.code === '23505' && error?.message?.includes('idx_items_number_org')) {
          // Retry with a temporary item number to force trigger regeneration
          const retryData = {
            ...insertData,
            item_number: `temp-${Date.now()}-${Math.random()?.toString(36)?.substr(2, 9)}`
          };
          
          console.log('Retrying with temporary item number:', retryData);
          
          const { data: retryResult, error: retryError } = await supabase?.from('items')?.insert(retryData)?.select()?.single();
            
          if (retryError) {
            console.error('Retry failed:', retryError);
            return { data: null, error: retryError };
          }
          
          return { data: retryResult, error: null };
        } else if (error?.code === '23502') {
          // Enhanced null constraint error handling
          const columnMatch = error?.message?.match(/null value in column "([^"]+)"/);
          const columnName = columnMatch ? columnMatch?.[1] : 'unknown';
          
          return { 
            data: null, 
            error: { 
              ...error, 
              message: `Required field '${columnName}' is missing. Please ensure all required fields are filled.` 
            } 
          };
        }
        
        return { data: null, error };
      }

      console.log('Item created successfully:', data);
      return { data, error: null };
    } catch (error) {
      console.error('Error in createItem:', error);
      return { 
        data: null, 
        error: { 
          code: 'UNEXPECTED_ERROR',
          message: error?.message || 'An unexpected error occurred while creating the item'
        } 
      };
    }
  }

  // Update an existing item
  async updateItem(itemId, itemData) {
    try {
      const { data, error } = await supabase?.from('items')?.update({
          item_name: itemData?.name,
          description: itemData?.description,
          item_number: itemData?.sku,
          category: itemData?.category,
          selling_price: itemData?.price,
          purchase_price: itemData?.cost,
          unit_of_measure: itemData?.unit || 'each',
          tax_rate: itemData?.taxRate || 0,
          weight_per_unit: itemData?.weight || 1,
          account_id: itemData?.accountId || null, // Add account ID field to update
          updated_at: new Date()?.toISOString()
        })?.eq('id', itemId)?.select()?.single();

      if (error) {
        console.error('Error updating item:', error);
        return { data: null, error };
      }

      return { data, error: null };
    } catch (error) {
      console.error('Error in updateItem:', error);
      return { data: null, error };
    }
  }

  // Delete an item (hard delete)
  async deleteItem(itemId) {
    try {
      const { error } = await supabase?.from('items')?.delete()?.eq('id', itemId);

      if (error) {
        console.error('Error deleting item:', error);
        return { error };
      }

      return { error: null };
    } catch (error) {
      console.error('Error in deleteItem:', error);
      return { error };
    }
  }

  // Archive an item (soft delete)
  async archiveItem(itemId) {
    try {
      const { data, error } = await supabase?.from('items')?.update({ 
          is_active: false,
          updated_at: new Date()?.toISOString()
        })?.eq('id', itemId)?.select()?.single();

      if (error) {
        console.error('Error archiving item:', error);
        return { data: null, error };
      }

      return { data, error: null };
    } catch (error) {
      console.error('Error in archiveItem:', error);
      return { data: null, error };
    }
  }

  // Bulk operations
  async bulkDelete(itemIds) {
    try {
      const { error } = await supabase?.from('items')?.delete()?.in('id', itemIds);

      if (error) {
        console.error('Error bulk deleting items:', error);
        return { error };
      }

      return { error: null };
    } catch (error) {
      console.error('Error in bulkDelete:', error);
      return { error };
    }
  }

  async bulkArchive(itemIds) {
    try {
      const { error } = await supabase?.from('items')?.update({ 
          is_active: false,
          updated_at: new Date()?.toISOString()
        })?.in('id', itemIds);

      if (error) {
        console.error('Error bulk archiving items:', error);
        return { error };
      }

      return { error: null };
    } catch (error) {
      console.error('Error in bulkArchive:', error);
      return { error };
    }
  }

  // NEW: Get chart of accounts for dropdown
  async getChartOfAccounts(organizationId) {
    try {
      const { data, error } = await supabase?.from('chart_of_accounts')
        ?.select('id, account_code, account_name, account_subtype, description')
        ?.eq('organization_id', organizationId)
        ?.eq('status', 'active')
        ?.order('account_code');

      if (error) {
        console.error('Error fetching chart of accounts:', error);
        return [];
      }

      return data || [];
    } catch (error) {
      console.error('Error in getChartOfAccounts:', error);
      return [];
    }
  }

  // Export items to CSV format
  generateCSVData(items) {
    const headers = [
      'Name', 'Description', 'SKU', 'Category', 'Price', 'Cost', 
      'Stock', 'Unit', 'Tax Rate', 'Weight', 'Status', 'Created At'
    ];

    const csvData = items?.map(item => [
      item?.name || '',
      item?.description || '',
      item?.sku || '',
      item?.category || '',
      item?.price || 0,
      item?.cost || 0,
      item?.stock || 0,
      item?.unit || 'each',
      item?.taxRate || 0,
      item?.weight || 0,
      item?.status || 'active',
      new Date(item?.createdAt)?.toLocaleDateString() || ''
    ]) || [];

    return [headers, ...csvData];
  }

  // Real-time subscription for items changes
  subscribeToItems(organizationId, callback) {
    return supabase?.channel('items_changes')?.on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'items',
          filter: `organization_id=eq.${organizationId}`
        },
        callback
      )?.subscribe();
  }

  // Get item usage analytics
  async getItemUsageAnalytics(itemId, days = 30) {
    try {
      let startDate = new Date();
      startDate?.setDate(startDate?.getDate() - days);

      const [salesData, quoteData] = await Promise.all([
        supabase?.from('invoice_line_items')?.select('quantity, unit_price, created_at')?.eq('item_id', itemId)?.gte('created_at', startDate?.toISOString())?.then(result => result?.data || []),
        supabase?.from('quote_line_items')?.select('quantity, unit_price, created_at')?.eq('item_id', itemId)?.gte('created_at', startDate?.toISOString())?.then(result => result?.data || [])
      ]);

      return {
        salesCount: salesData?.length,
        quotesCount: quoteData?.length,
        totalRevenue: salesData?.reduce((sum, sale) => 
          sum + (parseFloat(sale?.quantity || 0) * parseFloat(sale?.unit_price || 0)), 0),
        avgQuantityPerSale: salesData?.length > 0 
          ? salesData?.reduce((sum, sale) => sum + parseFloat(sale?.quantity || 0), 0) / salesData?.length
          : 0
      };
    } catch (error) {
      console.error('Error getting item usage analytics:', error);
      return {
        salesCount: 0,
        quotesCount: 0,
        totalRevenue: 0,
        avgQuantityPerSale: 0
      };
    }
  }
}

export const itemsManagementService = new ItemsManagementService();
export default itemsManagementService;